// src/app/services/laboratorio/laboratorio.service.ts

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ParametroLaboratorio } from '../../interfaces/laboratorio/parametro-laboratorio.interface';
import { ResultadoExtraido, ResultadoLaboratorio } from '../../interfaces/laboratorio/resultado-laboratorio.interface';
import { HistoricoLaboratorio } from '../../interfaces/laboratorio/historico-laboratorio.interface';
import { LaboratorioStorageService } from './laboratorio-storage.service';

@Injectable({
  providedIn: 'root',
})
export class LaboratorioService {
  private apiUrl = `${environment.apiUrl}/api`;
  private useLocalStorage = environment.useLocalStorage;

  constructor(private http: HttpClient, private storageService: LaboratorioStorageService) {
    console.log(`🔧 LaboratorioService modo: ${this.useLocalStorage ? 'LOCALSTORAGE' : 'API'}`);
  }

  /**
   * Obtener catálogo de parámetros de laboratorio
   */
  async getParametros(categoria?: string): Promise<ParametroLaboratorio[]> {
    if (this.useLocalStorage) {
      return this.storageService.getParametros(categoria);
    }

    // Modo API
    const url = categoria
      ? `${this.apiUrl}/parametros-laboratorio?categoria=${categoria}`
      : `${this.apiUrl}/parametros-laboratorio`;

    return await firstValueFrom(this.http.get<ParametroLaboratorio[]>(url));
  }

  /**
   * Guardar resultados de laboratorio extraídos
   */
  async guardarResultados(
    appointmentId: number,
    paraclinicoUuid: string,
    resultados: ResultadoExtraido[],
  ): Promise<any> {
    if (this.useLocalStorage) {
      return this.storageService.guardarResultados(appointmentId, paraclinicoUuid, resultados);
    }

    // Modo API
    const url = `${this.apiUrl}/appointments/${appointmentId}/paraclinicos/${paraclinicoUuid}/resultados`;

    return await firstValueFrom(this.http.post<any>(url, { resultados }));
  }

  /**
   * Obtener historial de laboratorios de un paciente
   */
  async getHistorico(pacienteId: number, categoria?: string): Promise<HistoricoLaboratorio> {
    if (this.useLocalStorage) {
      return this.storageService.getHistorico(pacienteId, categoria);
    }

    // Modo API
    const url = categoria
      ? `${this.apiUrl}/patients/${pacienteId}/laboratorios/historico?categoria=${categoria}`
      : `${this.apiUrl}/patients/${pacienteId}/laboratorios/historico`;

    return await firstValueFrom(this.http.get<HistoricoLaboratorio>(url));
  }

  /**
   * Obtener historial de un parámetro específico
   */
  async getHistoricoParametro(pacienteId: number, parametroId: number): Promise<any> {
    if (this.useLocalStorage) {
      return this.storageService.getHistoricoParametro(pacienteId, parametroId);
    }

    // Modo API
    const url = `${this.apiUrl}/patients/${pacienteId}/parametro/${parametroId}/historico`;

    return await firstValueFrom(this.http.get<any>(url));
  }

  /**
   * Editar resultado individual
   */
  async editarResultado(resultadoId: number, datos: Partial<ResultadoLaboratorio>): Promise<any> {
    if (this.useLocalStorage) {
      return this.storageService.editarResultado(resultadoId, datos);
    }

    // Modo API
    const url = `${this.apiUrl}/resultados-laboratorio/${resultadoId}`;

    return await firstValueFrom(this.http.put<any>(url, datos));
  }

  // ==================== UTILIDADES DE DESARROLLO ====================

  /**
   * Obtener estadísticas (solo localStorage)
   */
  getEstadisticas(): any {
    if (this.useLocalStorage) {
      return this.storageService.getEstadisticas();
    }
    return null;
  }

  /**
   * Limpiar datos (solo para desarrollo)
   */
  async limpiarDatos(): Promise<void> {
    if (this.useLocalStorage) {
      await this.storageService.limpiarResultados();
    }
  }
}
